import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  CreditCard,
  ExternalLink,
  LayoutDashboard,
  LogOut,
  ScrollText,
  Search,
  ShieldCheck,
  Store,
  Users,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { apiGet, apiPatch } from "@/api/client";
import { useProjectLocal } from "@/context/AppContext";
import { LogoMark, EmptyState, LoadingScreen, Money } from "@/components/project/Primitives";
import { CURRENCY_OPTIONS, formatMoney, getCurrencyMeta } from "@/lib/currency";
import { toast } from "@/components/ui/sonner";

const tone = {
  green: "bg-[#EAF2E8] text-[#173B2F]",
  yellow: "bg-orange-100 text-orange-700",
  red: "bg-red-100 text-red-700",
};

const CHART_COLORS = ["#173B2F", "#3F6F5C", "#7BA28F", "#C4A35A", "#D97706", "#B45309", "#9CA3AF", "#6B7280"];
const PAGE_SIZE = 20;

const nav = [
  ["dashboard", "Dashboard", LayoutDashboard, "/admin"],
  ["merchants", "Merchants", Store, "/admin/merchants"],
  ["owners", "Owners", Users, "/admin/owners"],
  ["payments", "Payments", CreditCard, "/admin/payments"],
  ["users", "Users", Users, "/admin/users"],
  ["analytics", "Analytics", BarChart3, "/admin/analytics"],
  ["audit", "Audit", ClipboardList, "/admin/audit"],
  ["logs", "Logs", ScrollText, "/admin/logs"],
];

function formatWhen(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function revenueLabel(revenueByCurrency = {}) {
  const parts = Object.entries(revenueByCurrency).map(([code, amount]) => formatMoney(amount, code));
  return parts.length ? parts.join(" · ") : "—";
}

function shortDate(value) {
  const raw = String(value || "");
  return raw.length >= 10 ? raw.slice(5) : raw;
}

export function Admin({ section = "dashboard" }) {
  const { user, loading, isAuthenticated, logout } = useProjectLocal();
  const navigate = useNavigate();
  const { merchantId, logId, auditId } = useParams();
  const activeSection =
    section === "merchant-detail"
      ? "merchants"
      : section === "log-detail"
        ? "logs"
        : section === "audit-detail"
          ? "audit"
          : section;

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) {
    return <AdminLocked title="Log in as platform admin" body="Use the admin credentials to open the operator console." />;
  }
  if (user?.role !== "platform_admin") {
    return <AdminLocked title="Operator access required" body="Merchant accounts cannot open this internal console." />;
  }

  return (
    <main className="relative min-h-screen bg-[#F8F5EE]" data-testid="admin-console-page">
      <div className="dashboard-grid">
        <aside className="sticky top-0 hidden h-screen flex-col border-r bg-white/86 p-5 lg:flex" data-testid="admin-sidebar">
          <div className="shrink-0">
            <LogoMark />
            <div className="mt-5 rounded-lg border bg-[#F8F5EE] p-3" data-testid="admin-role-card">
              <p className="text-xs font-bold uppercase text-muted-foreground">Access</p>
              <p className="font-extrabold">Platform admin</p>
            </div>
          </div>
          <nav className="mt-8 min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain pr-1" data-testid="admin-sidebar-nav">
            {nav.map(([key, label, Icon, href]) => (
              <Link
                key={key}
                data-testid={`admin-nav-${key}-link`}
                to={href}
                className={`flex tap-target items-center gap-3 rounded-lg px-3 text-sm font-bold transition-colors ${
                  activeSection === key ? "bg-[#173B2F] text-white" : "hover:bg-secondary"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Link>
            ))}
          </nav>
          <button
            data-testid="admin-console-logout-button"
            type="button"
            className="mt-4 inline-flex w-full shrink-0 tap-target items-center gap-3 rounded-lg border px-3 py-2 text-sm font-bold transition-colors hover:bg-secondary"
            onClick={async () => {
              await logout();
              navigate("/login", { replace: true });
            }}
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </aside>

        <section className="relative z-10 min-w-0 safe-bottom bg-[#F8F5EE] pb-20 lg:pb-0" data-testid="admin-main-area">
          <header className="sticky top-0 z-30 border-b bg-white/88 backdrop-blur-xl" data-testid="admin-console-header">
            <div className="flex min-h-16 flex-wrap items-center justify-between gap-3 px-4 md:px-8">
              <div>
                <p className="text-xs font-extrabold uppercase text-muted-foreground">BuildHof</p>
                <h1 className="text-lg font-extrabold md:text-2xl" data-testid="admin-console-title">
                  Operator Console
                </h1>
              </div>
              <Badge className={tone.green}>
                {section === "merchant-detail"
                  ? "Merchant detail"
                  : section === "log-detail"
                    ? "Log detail"
                    : section === "audit-detail"
                      ? "Audit detail"
                      : nav.find(([key]) => key === section)?.[1] || "Dashboard"}
              </Badge>
            </div>
          </header>

          <div className="mx-auto w-full max-w-7xl p-4 md:p-8" data-testid="admin-section-wrap">
            {section === "dashboard" && <DashboardSection />}
            {section === "merchants" && <MerchantsSection />}
            {section === "merchant-detail" && <MerchantDetailPage merchantId={merchantId} />}
            {section === "owners" && <OwnersSection />}
            {section === "payments" && <PaymentsSection />}
            {section === "users" && <UsersSection />}
            {section === "analytics" && <AnalyticsSection />}
            {section === "audit" && <AuditSection />}
            {section === "audit-detail" && <AuditDetailPage auditId={auditId} />}
            {section === "logs" && <LogsSection />}
            {section === "log-detail" && <LogDetailPage logId={logId} />}
          </div>
        </section>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-40 flex gap-1 overflow-x-auto border-t bg-white/95 p-2 backdrop-blur lg:hidden" data-testid="admin-mobile-nav">
        {nav.map(([key, label, Icon, href]) => (
          <Link
            key={key}
            to={href}
            data-testid={`admin-mobile-nav-${key}-link`}
            className={`flex min-w-[4.5rem] flex-1 flex-col items-center gap-1 rounded-lg px-1 py-2 text-[10px] font-bold ${
              activeSection === key ? "bg-[#173B2F] text-white" : "text-muted-foreground"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </Link>
        ))}
      </nav>
    </main>
  );
}

function AdminLocked({ title, body }) {
  return (
    <main className="page-shell grid min-h-screen place-items-center p-6" data-testid="admin-locked-page">
      <section className="max-w-lg rounded-lg border bg-white p-8 text-center">
        <ShieldCheck className="mx-auto h-10 w-10 text-primary" />
        <h1 className="mt-4 text-3xl font-extrabold">{title}</h1>
        <p className="mt-2 text-muted-foreground">{body}</p>
        <Link to="/login">
          <Button className="mt-5 rounded-lg">Go to login</Button>
        </Link>
      </section>
    </main>
  );
}

function Metric({ label, value, testId, detail }) {
  return (
    <article className="rounded-lg border bg-white p-4 shadow-sm" data-testid={`${testId}-card`}>
      <p className="text-xs font-extrabold uppercase text-muted-foreground">{label}</p>
      <p className="mt-1 break-words text-3xl font-extrabold" data-testid={`${testId}-value`}>
        {value}
      </p>
      {detail ? <p className="mt-1 text-xs text-muted-foreground">{detail}</p> : null}
    </article>
  );
}

function FilterSelect({ id, value, onChange, options, testId, label = "Filter" }) {
  return (
    <div className="min-w-[150px]">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <select
        id={id}
        data-testid={testId}
        className="h-12 w-full rounded-lg border bg-white px-3 text-sm font-bold"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function SearchBar({ query, setQuery, onSearch, placeholder, testId }) {
  return (
    <div className="flex min-w-[220px] flex-1 gap-2" data-testid={`${testId}-search`}>
      <div className="flex flex-1 items-center rounded-lg border bg-white px-3">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          data-testid={`${testId}-search-input`}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") onSearch();
          }}
          className="border-0 shadow-none focus-visible:ring-0"
          placeholder={placeholder}
        />
      </div>
      <Button data-testid={`${testId}-search-button`} className="rounded-lg" onClick={onSearch}>
        Search
      </Button>
    </div>
  );
}

function Pagination({ page, totalPages, total, limit, hasPrev, hasNext, onPageChange, testId }) {
  if (!total) return null;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-testid={`${testId}-pagination`}>
      <p className="text-sm text-muted-foreground">
        Showing {start}–{end} of {total}
      </p>
      <div className="flex items-center gap-2">
        <Button variant="outline" className="rounded-lg" disabled={!hasPrev} onClick={() => onPageChange(page - 1)} data-testid={`${testId}-pagination-prev`}>
          <ChevronLeft className="h-4 w-4" />
          Prev
        </Button>
        <span className="text-sm font-bold">
          Page {page} / {totalPages}
        </span>
        <Button variant="outline" className="rounded-lg" disabled={!hasNext} onClick={() => onPageChange(page + 1)} data-testid={`${testId}-pagination-next`}>
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <section className="rounded-lg border bg-white p-4 shadow-sm" data-testid={testId}>
      <h3 className="mb-3 font-extrabold">{title}</h3>
      <div className="h-64 w-full">{children}</div>
    </section>
  );
}

function DashboardSection() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setError("");
        setData(await apiGet("/admin/dashboard"));
      } catch {
        setError("Could not load dashboard.");
      }
    })();
  }, []);

  if (!data && !error) return <p className="text-sm text-muted-foreground">Loading dashboard…</p>;
  if (error) return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>;

  const charts = data.charts || {};

  return (
    <section className="space-y-6" data-testid="admin-dashboard-section">
      <div>
        <h2 className="text-2xl font-extrabold">Dashboard</h2>
        <p className="mt-1 text-sm text-muted-foreground">Owners, payments, and platform trends.</p>
      </div>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Businesses" value={data.businesses?.total ?? "—"} testId="admin-dash-businesses" />
        <Metric label="Active subs" value={data.businesses?.active_subscriptions ?? "—"} testId="admin-dash-active-subs" />
        <Metric label="Owners" value={data.owners?.total ?? "—"} testId="admin-dash-owners" detail={`${data.owners?.subscribed ?? 0} subscribed`} />
        <Metric label="Payments" value={data.payments?.total ?? "—"} testId="admin-dash-payments" detail={revenueLabel(data.payments?.revenue_by_currency)} />
      </section>

      <div className="grid gap-5 xl:grid-cols-2">
        <ChartCard title="Payments (30 days)" testId="admin-chart-payments-over-time">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={charts.payments_over_time || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={24} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="count" name="Payments" stroke="#173B2F" fill="#EAF2E8" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Owner signups (30 days)" testId="admin-chart-owners-over-time">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.owners_over_time || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={24} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="Owners" fill="#173B2F" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Subscription mix" testId="admin-chart-subscription-mix">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={charts.subscription_mix || []} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                {(charts.subscription_mix || []).map((entry, index) => (
                  <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Payment status" testId="admin-chart-payment-status">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={charts.payment_status_mix || []} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                {(charts.payment_status_mix || []).map((entry, index) => (
                  <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Merchant readiness" testId="admin-chart-readiness-mix">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={charts.readiness_mix || []} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                {(charts.readiness_mix || []).map((entry) => (
                  <Cell
                    key={entry.name}
                    fill={entry.name === "green" ? "#173B2F" : entry.name === "yellow" ? "#D97706" : entry.name === "red" ? "#DC2626" : "#9CA3AF"}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Revenue by currency" testId="admin-chart-revenue-currency">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.revenue_by_currency || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value, _name, item) => formatMoney(value, item?.payload?.name)} />
              <Bar dataKey="value" name="Revenue" fill="#3F6F5C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <section className="rounded-lg border bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="font-extrabold">Recent owners</h3>
            <Link to="/admin/owners" className="text-sm font-bold text-[#173B2F]">
              View all
            </Link>
          </div>
          <div className="grid gap-2">
            {data.owners?.recent?.map((owner) => (
              <article key={owner.id} className="rounded-lg border bg-[#F8F5EE] p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-extrabold">{owner.name}</p>
                    <p className="text-sm text-muted-foreground">{owner.email}</p>
                    <p className="mt-1 text-sm font-bold">{owner.business ? owner.business.name : "No business"}</p>
                  </div>
                  <Badge className={tone[owner.business?.subscription_status === "active" ? "green" : "yellow"]}>
                    {owner.business?.subscription_status || "none"}
                  </Badge>
                </div>
              </article>
            ))}
            {!data.owners?.recent?.length ? <EmptyState title="No owners yet" body="Owner accounts will appear here." testId="admin-dashboard-owners-empty" /> : null}
          </div>
        </section>

        <section className="rounded-lg border bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="font-extrabold">Recent payments</h3>
            <Link to="/admin/payments" className="text-sm font-bold text-[#173B2F]">
              View all
            </Link>
          </div>
          <div className="grid gap-2">
            {data.payments?.recent?.map((payment) => (
              <article key={payment.id} className="rounded-lg border bg-[#F8F5EE] p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-extrabold">
                      <Money amount={payment.amount} currency={payment.currency} />
                    </p>
                    <p className="text-sm font-bold">
                      {payment.business?.name || payment.business_id} · {payment.owner?.email || "Unknown owner"}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatWhen(payment.created_at)}</p>
                  </div>
                  <Badge className={tone[payment.status === "paid" ? "green" : "yellow"]}>{payment.status}</Badge>
                </div>
              </article>
            ))}
            {!data.payments?.recent?.length ? <EmptyState title="No payments yet" body="Owner subscription payments will appear here." testId="admin-dashboard-payments-empty" /> : null}
          </div>
        </section>
      </div>
    </section>
  );
}

function MerchantsSection() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [readiness, setReadiness] = useState("");
  const [published, setPublished] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  const load = async ({
    q = query,
    sub = subscriptionStatus,
    ready = readiness,
    pub = published,
    nextPage = page,
  } = {}) => {
    try {
      setError("");
      const params = new URLSearchParams({ page: String(nextPage), limit: String(PAGE_SIZE) });
      if (q) params.set("q", q);
      if (sub) params.set("subscription_status", sub);
      if (ready) params.set("readiness", ready);
      if (pub) params.set("published", pub);
      setData(await apiGet(`/admin/merchants?${params}`));
    } catch {
      setError("Could not load merchants.");
    }
  };

  useEffect(() => {
    load({ nextPage: 1 });
  }, []);

  if (!data && !error) return <p className="text-sm text-muted-foreground">Loading merchants…</p>;

  return (
    <section className="space-y-5" data-testid="admin-merchants-section">
      <div>
        <h2 className="text-2xl font-extrabold">Merchants</h2>
        <p className="mt-1 text-sm text-muted-foreground">Search, filter, and open merchant details.</p>
      </div>
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Metric label="Merchants" value={data?.total ?? "—"} testId="admin-merchants-total" />
        <Metric label="Active subs" value={data?.active ?? "—"} testId="admin-merchants-active" />
        <Metric label="Published" value={data?.published ?? "—"} testId="admin-merchants-published" />
        <Metric label="Ready" value={data?.readiness_green ?? "—"} testId="admin-merchants-ready" />
        <Metric label="At risk" value={(data?.readiness_yellow || 0) + (data?.readiness_red || 0)} testId="admin-merchants-risk" />
      </section>

      <section className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          <FilterSelect
            id="admin-merchants-sub"
            testId="admin-merchants-subscription-filter"
            label="Subscription"
            value={subscriptionStatus}
            onChange={(value) => {
              setSubscriptionStatus(value);
              setPage(1);
              load({ sub: value, nextPage: 1 });
            }}
            options={[
              { value: "", label: "All subscriptions" },
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
              { value: "unknown", label: "Unknown" },
            ]}
          />
          <FilterSelect
            id="admin-merchants-readiness"
            testId="admin-merchants-readiness-filter"
            label="Readiness"
            value={readiness}
            onChange={(value) => {
              setReadiness(value);
              setPage(1);
              load({ ready: value, nextPage: 1 });
            }}
            options={[
              { value: "", label: "All readiness" },
              { value: "green", label: "Green" },
              { value: "yellow", label: "Yellow" },
              { value: "red", label: "Red" },
            ]}
          />
          <FilterSelect
            id="admin-merchants-published"
            testId="admin-merchants-published-filter"
            label="Published"
            value={published}
            onChange={(value) => {
              setPublished(value);
              setPage(1);
              load({ pub: value, nextPage: 1 });
            }}
            options={[
              { value: "", label: "All publish states" },
              { value: "true", label: "Published" },
              { value: "false", label: "Draft" },
            ]}
          />
          <SearchBar
            query={query}
            setQuery={setQuery}
            onSearch={() => {
              setPage(1);
              load({ nextPage: 1 });
            }}
            placeholder="Search name, slug, email, or owner"
            testId="admin-merchants"
          />
        </div>
        {error ? <p className="mt-3 text-sm font-bold text-red-700">{error}</p> : null}

        <div className="mt-4 overflow-x-auto" data-testid="admin-merchants-list">
          <table className="w-full min-w-[860px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-muted-foreground">
                <th className="px-2 py-3">Merchant</th>
                <th className="px-2 py-3">Owner</th>
                <th className="px-2 py-3">Subscription</th>
                <th className="px-2 py-3">Readiness</th>
                <th className="px-2 py-3">Published</th>
                <th className="px-2 py-3">Updated</th>
                <th className="px-2 py-3" />
              </tr>
            </thead>
            <tbody>
              {data?.items?.map((merchant) => (
                <tr key={merchant.id} className="border-b last:border-0" data-testid={`admin-merchant-${merchant.id}`}>
                  <td className="px-2 py-3">
                    <p className="font-extrabold">{merchant.name}</p>
                    <p className="text-muted-foreground">/{merchant.slug}</p>
                  </td>
                  <td className="px-2 py-3">{merchant.owner?.email || "—"}</td>
                  <td className="px-2 py-3">
                    <Badge className={tone[merchant.subscription_status === "active" ? "green" : "yellow"]}>{merchant.subscription_status}</Badge>
                  </td>
                  <td className="px-2 py-3">
                    <Badge className={tone[merchant.readiness] || tone.yellow}>{merchant.readiness}</Badge>
                  </td>
                  <td className="px-2 py-3">{merchant.published ? "Yes" : "No"}</td>
                  <td className="px-2 py-3 text-muted-foreground">{formatWhen(merchant.updated_at)}</td>
                  <td className="px-2 py-3 text-right">
                    <Link to={`/admin/merchants/${merchant.id}`} data-testid={`admin-merchant-${merchant.id}-link`}>
                      <Button variant="outline" className="rounded-lg">
                        Details
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && !data.items?.length ? <EmptyState title="No merchants found" body="Try another search or filter." testId="admin-merchants-empty" /> : null}
        </div>
        <Pagination
          page={data?.page || 1}
          totalPages={data?.total_pages || 1}
          total={data?.total || 0}
          limit={data?.limit || PAGE_SIZE}
          hasPrev={data?.has_prev}
          hasNext={data?.has_next}
          testId="admin-merchants"
          onPageChange={(next) => {
            setPage(next);
            load({ nextPage: next });
          }}
        />
      </section>
    </section>
  );
}

function MerchantDetailPage({ merchantId }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [currencyDraft, setCurrencyDraft] = useState("GBP");
  const [savingCurrency, setSavingCurrency] = useState(false);

  const load = async () => {
    try {
      setError("");
      const result = await apiGet(`/admin/businesses/${merchantId}`);
      setDetail(result);
      setCurrencyDraft(result.business?.currency || "GBP");
    } catch {
      setError("Could not load merchant details.");
    }
  };

  useEffect(() => {
    if (merchantId) load();
  }, [merchantId]);

  if (!detail && !error) return <p className="text-sm text-muted-foreground">Loading merchant…</p>;
  if (error) {
    return (
      <section className="space-y-4">
        <Link to="/admin/merchants" className="inline-flex items-center gap-2 text-sm font-bold text-[#173B2F]">
          <ArrowLeft className="h-4 w-4" />
          Back to merchants
        </Link>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>
      </section>
    );
  }

  const business = detail.business;
  const diagnostics = detail.diagnostics;
  const currencyMeta = getCurrencyMeta(business.currency);

  const saveCurrency = async () => {
    setSavingCurrency(true);
    try {
      await apiPatch(`/admin/businesses/${business.id}`, { currency: currencyDraft });
      toast.success(`Currency updated to ${getCurrencyMeta(currencyDraft).name}`);
      await load();
    } catch {
      toast.error("Could not update currency");
    } finally {
      setSavingCurrency(false);
    }
  };

  const copyStorefront = async () => {
    const url = `${window.location.origin}/${business.slug}`;
    try {
      await navigator.clipboard?.writeText(url);
      toast.success("Storefront link copied");
    } catch {
      window.prompt("Copy storefront link", url);
      toast.message("Copy link manually from the prompt");
    }
  };

  return (
    <section className="space-y-5" data-testid="admin-merchant-detail-page">
      <Link to="/admin/merchants" className="inline-flex items-center gap-2 text-sm font-bold text-[#173B2F]" data-testid="admin-merchant-back-link">
        <ArrowLeft className="h-4 w-4" />
        Back to merchants
      </Link>

      <section className="rounded-lg border bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-extrabold uppercase text-muted-foreground">{business.type_id}</p>
            <h2 className="text-3xl font-extrabold">{business.name}</h2>
            <p className="text-sm text-muted-foreground">/{business.slug}</p>
            <p className="mt-2 text-sm font-bold">
              Store currency: {currencyMeta.symbol} {currencyMeta.code} · {currencyMeta.name}
            </p>
            {detail.owner ? (
              <p className="mt-1 text-sm text-muted-foreground">
                Owner: {detail.owner.name} · {detail.owner.email}
              </p>
            ) : null}
          </div>
          <Badge className={tone[diagnostics.readiness]}>{diagnostics.readiness}</Badge>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to={`/${business.slug}`} target="_blank">
            <Button variant="outline" className="rounded-lg">
              <ExternalLink className="h-4 w-4" />
              Storefront
            </Button>
          </Link>
          <Button variant="outline" className="rounded-lg" onClick={copyStorefront}>
            Copy storefront link
          </Button>
        </div>
      </section>

      <section className="rounded-lg border bg-white p-5 shadow-sm">
        <h3 className="font-extrabold">Store currency</h3>
        <p className="mt-1 text-sm text-muted-foreground">Sets the symbol shown on menus, checkout, and dashboard prices.</p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[220px]">
            <label htmlFor="admin-currency-select" className="text-sm font-bold">
              Currency
            </label>
            <select
              id="admin-currency-select"
              data-testid="admin-currency-select"
              className="mt-2 h-12 w-full rounded-lg border bg-white px-3 font-bold"
              value={currencyDraft}
              onChange={(event) => setCurrencyDraft(event.target.value)}
            >
              {CURRENCY_OPTIONS.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.symbol} {option.code} · {option.name}
                </option>
              ))}
            </select>
          </div>
          <Button className="rounded-lg" disabled={savingCurrency || currencyDraft === business.currency} onClick={saveCurrency} data-testid="admin-currency-save-button">
            {savingCurrency ? "Saving…" : "Save currency"}
          </Button>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-3">
        <Metric label="Products" value={diagnostics.counts.products} testId="admin-detail-products" />
        <Metric label="Orders" value={diagnostics.counts.orders} testId="admin-detail-orders" />
        <Metric label="Staff" value={diagnostics.counts.staff} testId="admin-detail-staff" />
        <Metric label="Assets" value={diagnostics.counts.assets} testId="admin-detail-assets" />
        <Metric label="Imports" value={diagnostics.counts.imports} testId="admin-detail-imports" />
        <Metric label="Tickets" value={diagnostics.counts.open_support_tickets} testId="admin-detail-tickets" />
      </section>

      <section className="rounded-lg border bg-white p-5 shadow-sm">
        <h3 className="font-extrabold">Readiness flags</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {diagnostics.readiness_flags.length ? (
            diagnostics.readiness_flags.map((flag) => (
              <Badge key={flag.code} className={tone[flag.severity === "red" ? "red" : "yellow"]}>
                {flag.label}
              </Badge>
            ))
          ) : (
            <Badge className={tone.green}>All clear</Badge>
          )}
        </div>
      </section>

      {detail.staff?.length ? (
        <section className="rounded-lg border bg-white p-5 shadow-sm">
          <h3 className="font-extrabold">Staff</h3>
          <div className="mt-3 grid gap-2">
            {detail.staff.map((member) => (
              <article key={member.id} className="rounded-lg border bg-[#F8F5EE] p-3">
                <p className="font-bold">
                  {member.name} · {member.role}
                </p>
                <p className="text-sm text-muted-foreground">{member.email}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <Timeline title="Recent orders" items={detail.recent_orders} field="order_number" secondary="status" />
      <Timeline title="Recent imports" items={detail.recent_imports} field="filename" secondary="status" />
      <Timeline title="Recent tickets" items={detail.recent_tickets} field="ticket_number" secondary="subject" />
    </section>
  );
}

function Timeline({ title, items, field, secondary }) {
  return (
    <section className="rounded-lg border bg-white p-5 shadow-sm">
      <h3 className="font-extrabold">{title}</h3>
      <div className="mt-3 grid gap-2">
        {items?.length ? (
          items.map((item) => (
            <article key={item.id || item[field]} className="rounded-lg border bg-[#F8F5EE] p-3">
              <p className="font-bold">{item[field]}</p>
              <p className="text-sm text-muted-foreground">{item[secondary]}</p>
            </article>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">Nothing recent.</p>
        )}
      </div>
    </section>
  );
}

function OwnersSection() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  const load = async ({ q = query, st = status, sub = subscriptionStatus, nextPage = page } = {}) => {
    try {
      setError("");
      const params = new URLSearchParams({ page: String(nextPage), limit: String(PAGE_SIZE) });
      if (q) params.set("q", q);
      if (st) params.set("status", st);
      if (sub) params.set("subscription_status", sub);
      setData(await apiGet(`/admin/owners?${params}`));
    } catch {
      setError("Could not load owners.");
    }
  };

  useEffect(() => {
    load({ nextPage: 1 });
  }, []);

  if (!data && !error) return <p className="text-sm text-muted-foreground">Loading owners…</p>;

  return (
    <section className="space-y-5" data-testid="admin-owners-section">
      <div>
        <h2 className="text-2xl font-extrabold">Owners</h2>
        <p className="mt-1 text-sm text-muted-foreground">Business owners across the platform.</p>
      </div>
      <section className="grid gap-3 sm:grid-cols-3">
        <Metric label="Owners" value={data?.total ?? "—"} testId="admin-owners-total" />
        <Metric label="Active" value={data?.active ?? "—"} testId="admin-owners-active" />
        <Metric label="Subscribed" value={data?.subscribed ?? "—"} testId="admin-owners-subscribed" />
      </section>
      <section className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          <FilterSelect
            id="admin-owners-status"
            testId="admin-owners-status-filter"
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
              load({ st: value, nextPage: 1 });
            }}
            options={[
              { value: "", label: "All statuses" },
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ]}
          />
          <FilterSelect
            id="admin-owners-sub"
            testId="admin-owners-subscription-filter"
            value={subscriptionStatus}
            onChange={(value) => {
              setSubscriptionStatus(value);
              setPage(1);
              load({ sub: value, nextPage: 1 });
            }}
            options={[
              { value: "", label: "All subscriptions" },
              { value: "active", label: "Subscribed" },
              { value: "inactive", label: "Inactive" },
              { value: "unknown", label: "Unknown" },
            ]}
          />
          <SearchBar
            query={query}
            setQuery={setQuery}
            onSearch={() => {
              setPage(1);
              load({ nextPage: 1 });
            }}
            placeholder="Search owner, email, or store"
            testId="admin-owners"
          />
        </div>
        {error ? <p className="mt-3 text-sm font-bold text-red-700">{error}</p> : null}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-muted-foreground">
                <th className="px-2 py-3">Owner</th>
                <th className="px-2 py-3">Business</th>
                <th className="px-2 py-3">Status</th>
                <th className="px-2 py-3">Subscription</th>
                <th className="px-2 py-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {data?.items?.map((owner) => (
                <tr key={owner.id} className="border-b last:border-0">
                  <td className="px-2 py-3">
                    <p className="font-extrabold">{owner.name}</p>
                    <p className="text-muted-foreground">{owner.email}</p>
                  </td>
                  <td className="px-2 py-3">{owner.business ? `${owner.business.name} · /${owner.business.slug}` : "—"}</td>
                  <td className="px-2 py-3">
                    <Badge className={tone[(owner.status || "active") === "active" ? "green" : "yellow"]}>{owner.status || "active"}</Badge>
                  </td>
                  <td className="px-2 py-3">
                    <Badge className={tone[owner.business?.subscription_status === "active" ? "green" : "yellow"]}>
                      {owner.business?.subscription_status || "none"}
                    </Badge>
                  </td>
                  <td className="px-2 py-3 text-muted-foreground">{formatWhen(owner.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && !data.items?.length ? <EmptyState title="No owners found" body="Try a different search or filter." testId="admin-owners-empty" /> : null}
        </div>
        <Pagination
          page={data?.page || 1}
          totalPages={data?.total_pages || 1}
          total={data?.total || 0}
          limit={data?.limit || PAGE_SIZE}
          hasPrev={data?.has_prev}
          hasNext={data?.has_next}
          testId="admin-owners"
          onPageChange={(next) => {
            setPage(next);
            load({ nextPage: next });
          }}
        />
      </section>
    </section>
  );
}

function PaymentsSection() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [mocked, setMocked] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  const load = async ({ q = query, st = status, mock = mocked, nextPage = page } = {}) => {
    try {
      setError("");
      const params = new URLSearchParams({ page: String(nextPage), limit: String(PAGE_SIZE) });
      if (q) params.set("q", q);
      if (st) params.set("status", st);
      if (mock) params.set("mocked", mock);
      setData(await apiGet(`/admin/payments?${params}`));
    } catch {
      setError("Could not load payments.");
    }
  };

  useEffect(() => {
    load({ nextPage: 1 });
  }, []);

  if (!data && !error) return <p className="text-sm text-muted-foreground">Loading payments…</p>;

  return (
    <section className="space-y-5" data-testid="admin-payments-section">
      <div>
        <h2 className="text-2xl font-extrabold">Payments</h2>
        <p className="mt-1 text-sm text-muted-foreground">Subscription and addon payments from owners.</p>
      </div>
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Payments" value={data?.total ?? "—"} testId="admin-payments-total" />
        <Metric label="Paid" value={data?.paid ?? "—"} testId="admin-payments-paid" />
        <Metric label="Mocked" value={data?.mocked ?? "—"} testId="admin-payments-mocked" />
        <Metric label="Revenue" value={revenueLabel(data?.revenue_by_currency)} testId="admin-payments-revenue" />
      </section>
      <section className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          <FilterSelect
            id="admin-payments-status"
            testId="admin-payments-status-filter"
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
              load({ st: value, nextPage: 1 });
            }}
            options={[
              { value: "", label: "All statuses" },
              { value: "paid", label: "Paid" },
              { value: "failed", label: "Failed" },
              { value: "pending", label: "Pending" },
            ]}
          />
          <FilterSelect
            id="admin-payments-mocked"
            testId="admin-payments-mocked-filter"
            value={mocked}
            onChange={(value) => {
              setMocked(value);
              setPage(1);
              load({ mock: value, nextPage: 1 });
            }}
            options={[
              { value: "", label: "All sources" },
              { value: "true", label: "Mocked" },
              { value: "false", label: "Live" },
            ]}
          />
          <SearchBar
            query={query}
            setQuery={setQuery}
            onSearch={() => {
              setPage(1);
              load({ nextPage: 1 });
            }}
            placeholder="Search owner, store, package, or reference"
            testId="admin-payments"
          />
        </div>
        {error ? <p className="mt-3 text-sm font-bold text-red-700">{error}</p> : null}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-muted-foreground">
                <th className="px-2 py-3">Amount</th>
                <th className="px-2 py-3">Owner</th>
                <th className="px-2 py-3">Business</th>
                <th className="px-2 py-3">Package</th>
                <th className="px-2 py-3">Status</th>
                <th className="px-2 py-3">When</th>
              </tr>
            </thead>
            <tbody>
              {data?.items?.map((payment) => (
                <tr key={payment.id} className="border-b last:border-0">
                  <td className="px-2 py-3 font-extrabold">
                    <Money amount={payment.amount} currency={payment.currency} />
                  </td>
                  <td className="px-2 py-3">{payment.owner?.email || "—"}</td>
                  <td className="px-2 py-3">{payment.business?.name || payment.business_id}</td>
                  <td className="px-2 py-3">{payment.package_id || "—"}</td>
                  <td className="px-2 py-3">
                    <Badge className={tone[payment.status === "paid" ? "green" : "yellow"]}>{payment.status}</Badge>
                    {payment.mocked ? <Badge className={`ml-1 ${tone.yellow}`}>mocked</Badge> : null}
                  </td>
                  <td className="px-2 py-3 text-muted-foreground">{formatWhen(payment.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && !data.items?.length ? <EmptyState title="No payments yet" body="Owner subscription payments will appear here." testId="admin-payments-empty" /> : null}
        </div>
        <Pagination
          page={data?.page || 1}
          totalPages={data?.total_pages || 1}
          total={data?.total || 0}
          limit={data?.limit || PAGE_SIZE}
          hasPrev={data?.has_prev}
          hasNext={data?.has_next}
          testId="admin-payments"
          onPageChange={(next) => {
            setPage(next);
            load({ nextPage: next });
          }}
        />
      </section>
    </section>
  );
}

function UsersSection() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  const load = async ({ q = query, roleFilter = role, st = status, nextPage = page } = {}) => {
    try {
      setError("");
      const params = new URLSearchParams({ page: String(nextPage), limit: String(PAGE_SIZE) });
      if (q) params.set("q", q);
      if (roleFilter) params.set("role", roleFilter);
      if (st) params.set("status", st);
      setData(await apiGet(`/admin/users?${params}`));
    } catch {
      setError("Could not load users.");
    }
  };

  useEffect(() => {
    load({ nextPage: 1 });
  }, []);

  if (!data && !error) return <p className="text-sm text-muted-foreground">Loading users…</p>;

  return (
    <section className="space-y-5" data-testid="admin-users-section">
      <div>
        <h2 className="text-2xl font-extrabold">Owner users</h2>
        <p className="mt-1 text-sm text-muted-foreground">Owners, managers, and staff across merchant accounts.</p>
      </div>
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Users" value={data?.total ?? "—"} testId="admin-users-total" />
        <Metric label="Owners" value={data?.owners ?? "—"} testId="admin-users-owners" />
        <Metric label="Managers" value={data?.managers ?? "—"} testId="admin-users-managers" />
        <Metric label="Staff" value={data?.staff ?? "—"} testId="admin-users-staff" />
      </section>
      <section className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          <FilterSelect
            id="admin-users-role"
            testId="admin-users-role-filter"
            value={role}
            onChange={(value) => {
              setRole(value);
              setPage(1);
              load({ roleFilter: value, nextPage: 1 });
            }}
            options={[
              { value: "", label: "All roles" },
              { value: "owner", label: "Owners" },
              { value: "manager", label: "Managers" },
              { value: "staff", label: "Staff" },
            ]}
          />
          <FilterSelect
            id="admin-users-status"
            testId="admin-users-status-filter"
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
              load({ st: value, nextPage: 1 });
            }}
            options={[
              { value: "", label: "All statuses" },
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ]}
          />
          <SearchBar
            query={query}
            setQuery={setQuery}
            onSearch={() => {
              setPage(1);
              load({ nextPage: 1 });
            }}
            placeholder="Search name, email, role, or store"
            testId="admin-users"
          />
        </div>
        {error ? <p className="mt-3 text-sm font-bold text-red-700">{error}</p> : null}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[680px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-muted-foreground">
                <th className="px-2 py-3">Name</th>
                <th className="px-2 py-3">Email</th>
                <th className="px-2 py-3">Role</th>
                <th className="px-2 py-3">Business</th>
                <th className="px-2 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {data?.items?.map((user) => (
                <tr key={user.id} className="border-b last:border-0">
                  <td className="px-2 py-3 font-extrabold">{user.name}</td>
                  <td className="px-2 py-3">{user.email}</td>
                  <td className="px-2 py-3">
                    <Badge className={tone.green}>{user.role}</Badge>
                  </td>
                  <td className="px-2 py-3">{user.business ? user.business.name : "—"}</td>
                  <td className="px-2 py-3">
                    <Badge className={tone[(user.status || "active") === "active" ? "green" : "yellow"]}>{user.status || "active"}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && !data.items?.length ? <EmptyState title="No users found" body="Try another role or search." testId="admin-users-empty" /> : null}
        </div>
        <Pagination
          page={data?.page || 1}
          totalPages={data?.total_pages || 1}
          total={data?.total || 0}
          limit={data?.limit || PAGE_SIZE}
          hasPrev={data?.has_prev}
          hasNext={data?.has_next}
          testId="admin-users"
          onPageChange={(next) => {
            setPage(next);
            load({ nextPage: next });
          }}
        />
      </section>
    </section>
  );
}

function AnalyticsSection() {
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState(null);
  const [query, setQuery] = useState("");
  const [eventType, setEventType] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  const loadEvents = async (q = query, type = eventType, nextPage = page) => {
    const params = new URLSearchParams({ page: String(nextPage), limit: String(PAGE_SIZE) });
    if (q) params.set("q", q);
    if (type) params.set("event_type", type);
    setEvents(await apiGet(`/admin/analytics/events?${params}`));
  };

  useEffect(() => {
    (async () => {
      try {
        setError("");
        const [summaryResult] = await Promise.all([apiGet("/admin/analytics/summary"), loadEvents("", "", 1)]);
        setSummary(summaryResult);
      } catch {
        setError("Could not load analytics.");
      }
    })();
  }, []);

  if ((!summary || !events) && !error) return <p className="text-sm text-muted-foreground">Loading analytics…</p>;

  return (
    <section className="space-y-5" data-testid="admin-analytics-section">
      <div>
        <h2 className="text-2xl font-extrabold">Analytics</h2>
        <p className="mt-1 text-sm text-muted-foreground">Platform traffic, owner activity, and recent events.</p>
      </div>
      {error ? <p className="text-sm font-bold text-red-700">{error}</p> : null}
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Visitors today" value={summary?.visitors_today ?? "—"} testId="admin-analytics-visitors" />
        <Metric label="Conversion" value={`${summary?.conversion_rate ?? 0}%`} testId="admin-analytics-conversion" />
        <Metric label="Owner DAU" value={summary?.active_users?.owners?.dau ?? "—"} testId="admin-analytics-owner-dau" />
        <Metric label="Storefront DAU" value={summary?.active_users?.storefront?.dau ?? "—"} testId="admin-analytics-storefront-dau" />
      </section>
      <section className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          <FilterSelect
            id="admin-analytics-event-type"
            testId="admin-analytics-event-type"
            value={eventType}
            onChange={(value) => {
              setEventType(value);
              setPage(1);
              loadEvents(query, value, 1).catch(() => setError("Could not load events."));
            }}
            options={[{ value: "", label: "All events" }, ...(events?.event_types || []).map((type) => ({ value: type, label: type }))]}
          />
          <SearchBar
            query={query}
            setQuery={setQuery}
            onSearch={() => {
              setPage(1);
              loadEvents(query, eventType, 1).catch(() => setError("Could not load events."));
            }}
            placeholder="Search path, session, business, or user"
            testId="admin-analytics"
          />
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-muted-foreground">
                <th className="px-2 py-3">Event</th>
                <th className="px-2 py-3">Business</th>
                <th className="px-2 py-3">Path</th>
                <th className="px-2 py-3">Session</th>
                <th className="px-2 py-3">When</th>
              </tr>
            </thead>
            <tbody>
              {events?.items?.map((event) => (
                <tr key={event.id} className="border-b last:border-0">
                  <td className="px-2 py-3 font-extrabold">{event.event_type}</td>
                  <td className="px-2 py-3">{event.business?.name || event.business_id || "—"}</td>
                  <td className="px-2 py-3 text-muted-foreground">{event.path || "—"}</td>
                  <td className="px-2 py-3 text-muted-foreground">{event.session_id || "—"}</td>
                  <td className="px-2 py-3 text-muted-foreground">{formatWhen(event.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {events && !events.items?.length ? <EmptyState title="No events found" body="Tracked analytics events will appear here." testId="admin-analytics-empty" /> : null}
        </div>
        <Pagination
          page={events?.page || 1}
          totalPages={events?.total_pages || 1}
          total={events?.total || 0}
          limit={events?.limit || PAGE_SIZE}
          hasPrev={events?.has_prev}
          hasNext={events?.has_next}
          testId="admin-analytics"
          onPageChange={(next) => {
            setPage(next);
            loadEvents(query, eventType, next).catch(() => setError("Could not load events."));
          }}
        />
      </section>
    </section>
  );
}

function AuditSection() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  const load = async ({ q = query, src = source, nextPage = page } = {}) => {
    try {
      setError("");
      const params = new URLSearchParams({ page: String(nextPage), limit: String(PAGE_SIZE) });
      if (q) params.set("q", q);
      if (src) params.set("source", src);
      setData(await apiGet(`/admin/audit?${params}`));
    } catch {
      setError("Could not load audit log.");
    }
  };

  useEffect(() => {
    load({ nextPage: 1 });
  }, []);

  if (!data && !error) return <p className="text-sm text-muted-foreground">Loading audit log…</p>;

  return (
    <section className="space-y-5" data-testid="admin-audit-section">
      <div>
        <h2 className="text-2xl font-extrabold">Audit</h2>
        <p className="mt-1 text-sm text-muted-foreground">Admin actions, payments, and business creation activity.</p>
      </div>
      <section className="grid gap-3 sm:grid-cols-3">
        <Metric label="Events" value={data?.total ?? "—"} testId="admin-audit-total" />
        <Metric label="Admin actions" value={data?.admin_actions ?? "—"} testId="admin-audit-admin-actions" />
        <Metric label="Payments" value={data?.payments ?? "—"} testId="admin-audit-payments" />
      </section>
      <section className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          <FilterSelect
            id="admin-audit-source"
            testId="admin-audit-source-filter"
            value={source}
            onChange={(value) => {
              setSource(value);
              setPage(1);
              load({ src: value, nextPage: 1 });
            }}
            options={[
              { value: "", label: "All sources" },
              { value: "audit_logs", label: "Admin actions" },
              { value: "subscription_payments", label: "Payments" },
              { value: "businesses", label: "Business created" },
            ]}
          />
          <SearchBar
            query={query}
            setQuery={setQuery}
            onSearch={() => {
              setPage(1);
              load({ nextPage: 1 });
            }}
            placeholder="Search action, actor, or store"
            testId="admin-audit"
          />
        </div>
        {error ? <p className="mt-3 text-sm font-bold text-red-700">{error}</p> : null}
        <div className="mt-4 overflow-x-auto" data-testid="admin-audit-list">
          <table className="w-full min-w-[920px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-muted-foreground">
                <th className="px-2 py-3">When</th>
                <th className="px-2 py-3">Action</th>
                <th className="px-2 py-3">Summary</th>
                <th className="px-2 py-3">Actor</th>
                <th className="px-2 py-3">Business</th>
                <th className="px-2 py-3">Source</th>
                <th className="px-2 py-3" />
              </tr>
            </thead>
            <tbody>
              {data?.items?.map((entry) => (
                <tr key={entry.id} className="border-b last:border-0" data-testid={`admin-audit-${entry.id}`}>
                  <td className="whitespace-nowrap px-2 py-3 text-muted-foreground">{formatWhen(entry.created_at)}</td>
                  <td className="px-2 py-3 font-bold">{entry.action || "—"}</td>
                  <td className="max-w-[260px] truncate px-2 py-3 font-extrabold" title={entry.summary || entry.action || ""}>
                    {entry.summary || entry.action || "—"}
                  </td>
                  <td className="px-2 py-3 text-muted-foreground">{entry.actor_email || entry.actor_role || "system"}</td>
                  <td className="px-2 py-3">{entry.business?.name || "—"}</td>
                  <td className="px-2 py-3">
                    <Badge className={tone.green}>{entry.source || "audit"}</Badge>
                  </td>
                  <td className="px-2 py-3 text-right">
                    <Link to={`/admin/audit/${entry.id}`} data-testid={`admin-audit-${entry.id}-link`}>
                      <Button variant="outline" className="rounded-lg">
                        View
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && !data.items?.length ? (
            <EmptyState title="No audit events" body="Admin changes and payments will show up here." testId="admin-audit-empty" />
          ) : null}
        </div>
        <Pagination
          page={data?.page || 1}
          totalPages={data?.total_pages || 1}
          total={data?.total || 0}
          limit={data?.limit || PAGE_SIZE}
          hasPrev={data?.has_prev}
          hasNext={data?.has_next}
          testId="admin-audit"
          onPageChange={(next) => {
            setPage(next);
            load({ nextPage: next });
          }}
        />
      </section>
    </section>
  );
}

function AuditDetailPage({ auditId }) {
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError("");
        const result = await apiGet(`/admin/audit/${auditId}`);
        if (!cancelled) setEntry(result);
      } catch {
        if (!cancelled) {
          setEntry(null);
          setError("Could not load this audit entry.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auditId]);

  if (!entry && !error) return <p className="text-sm text-muted-foreground">Loading audit…</p>;

  if (error) {
    return (
      <section className="space-y-4" data-testid="admin-audit-detail-error">
        <Link to="/admin/audit" className="inline-flex items-center gap-2 text-sm font-bold text-[#173B2F]" data-testid="admin-audit-back-link">
          <ArrowLeft className="h-4 w-4" />
          Back to audit
        </Link>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>
      </section>
    );
  }

  return (
    <section className="space-y-5" data-testid="admin-audit-detail-page">
      <Link to="/admin/audit" className="inline-flex items-center gap-2 text-sm font-bold text-[#173B2F]" data-testid="admin-audit-back-link">
        <ArrowLeft className="h-4 w-4" />
        Back to audit
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-extrabold uppercase text-muted-foreground">{entry.action || "audit"}</p>
          <h2 className="break-words text-2xl font-extrabold">{entry.summary || entry.action || "Audit event"}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{formatWhen(entry.created_at)}</p>
        </div>
        <Badge className={tone.green}>{entry.source || "audit"}</Badge>
      </div>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Actor" value={entry.actor_email || entry.actor_role || "system"} testId="admin-audit-detail-actor" />
        <Metric label="Business" value={entry.business?.name || "—"} testId="admin-audit-detail-business" />
        <Metric label="Target" value={entry.target_type || "—"} testId="admin-audit-detail-target-type" />
        <Metric label="Target ID" value={entry.target_id || "—"} testId="admin-audit-detail-target-id" />
      </section>

      <section className="rounded-lg border bg-white p-4 shadow-sm">
        <h3 className="text-sm font-extrabold uppercase text-muted-foreground">Summary</h3>
        <dl className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-bold uppercase text-muted-foreground">Action</dt>
            <dd className="font-bold">{entry.action || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold uppercase text-muted-foreground">Actor role</dt>
            <dd className="font-bold">{entry.actor_role || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold uppercase text-muted-foreground">Actor ID</dt>
            <dd className="break-all font-mono text-sm">{entry.actor_id || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold uppercase text-muted-foreground">Business ID</dt>
            <dd className="break-all font-mono text-sm">{entry.business_id || "—"}</dd>
          </div>
          {entry.business?.slug ? (
            <div>
              <dt className="text-xs font-bold uppercase text-muted-foreground">Store slug</dt>
              <dd className="font-bold">/{entry.business.slug}</dd>
            </div>
          ) : null}
        </dl>
      </section>

      <section className="rounded-lg border bg-white p-4 shadow-sm">
        <h3 className="text-sm font-extrabold uppercase text-muted-foreground">Raw audit</h3>
        <pre className="mt-3 max-h-96 overflow-auto rounded-md border bg-[#F8F5EE] p-3 text-xs leading-relaxed text-[#173B2F]" data-testid="admin-audit-detail-raw">
          {JSON.stringify(entry, null, 2)}
        </pre>
      </section>
    </section>
  );
}

function logLevelTone(level) {
  const value = String(level || "").toUpperCase();
  if (value === "ERROR" || value === "FATAL") return tone.red;
  if (value === "WARN") return tone.yellow;
  return tone.green;
}

function LogsSection() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("");
  const [level, setLevel] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  const load = async ({ q = query, logType = type, logLevel = level, nextPage = page } = {}) => {
    try {
      setError("");
      const params = new URLSearchParams({ page: String(nextPage), limit: String(PAGE_SIZE) });
      if (q) params.set("q", q);
      if (logType) params.set("type", logType);
      if (logLevel) params.set("level", logLevel);
      setData(await apiGet(`/admin/logs?${params}`));
    } catch {
      setError("Could not load application logs.");
    }
  };

  useEffect(() => {
    load({ nextPage: 1 });
  }, []);

  if (!data && !error) return <p className="text-sm text-muted-foreground">Loading application logs…</p>;

  return (
    <section className="space-y-5" data-testid="admin-logs-section">
      <div>
        <h2 className="text-2xl font-extrabold">Logs</h2>
        <p className="mt-1 text-sm text-muted-foreground">HTTP requests and errors stored in the application log database.</p>
      </div>
      <section className="grid gap-3 sm:grid-cols-3">
        <Metric label="HTTP" value={data?.http_count ?? "—"} testId="admin-logs-http" />
        <Metric label="Errors" value={data?.error_count ?? "—"} testId="admin-logs-errors" />
        <Metric label="Warnings" value={data?.warn_count ?? "—"} testId="admin-logs-warns" />
      </section>
      <section className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          <FilterSelect
            id="admin-logs-type"
            testId="admin-logs-type-filter"
            value={type}
            onChange={(value) => {
              setType(value);
              setPage(1);
              load({ logType: value, nextPage: 1 });
            }}
            options={[
              { value: "", label: "All types" },
              { value: "http", label: "HTTP" },
              { value: "error", label: "Errors" },
            ]}
          />
          <FilterSelect
            id="admin-logs-level"
            testId="admin-logs-level-filter"
            value={level}
            onChange={(value) => {
              setLevel(value);
              setPage(1);
              load({ logLevel: value, nextPage: 1 });
            }}
            options={[
              { value: "", label: "All levels" },
              { value: "info", label: "INFO" },
              { value: "warn", label: "WARN" },
              { value: "error", label: "ERROR" },
            ]}
          />
          <SearchBar
            query={query}
            setQuery={setQuery}
            onSearch={() => {
              setPage(1);
              load({ nextPage: 1 });
            }}
            placeholder="Search path, message, requestId, user…"
            testId="admin-logs"
          />
        </div>
        {error ? <p className="mt-3 text-sm font-bold text-red-700">{error}</p> : null}
        <div className="mt-4 overflow-x-auto" data-testid="admin-logs-list">
          <table className="w-full min-w-[980px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-muted-foreground">
                <th className="px-2 py-3">When</th>
                <th className="px-2 py-3">Level</th>
                <th className="px-2 py-3">Type</th>
                <th className="px-2 py-3">Method / Status</th>
                <th className="px-2 py-3">Endpoint / Message</th>
                <th className="px-2 py-3">Duration</th>
                <th className="px-2 py-3">Request ID</th>
                <th className="px-2 py-3" />
              </tr>
            </thead>
            <tbody>
              {data?.items?.map((entry) => {
                const summary =
                  entry.type === "http"
                    ? entry.endpoint || entry.path || "/"
                    : entry.message || entry.error_name || "Error";
                return (
                  <tr key={entry.id} className="border-b last:border-0" data-testid={`admin-log-${entry.id}`}>
                    <td className="whitespace-nowrap px-2 py-3 text-muted-foreground">{formatWhen(entry.created_at)}</td>
                    <td className="px-2 py-3">
                      <Badge className={logLevelTone(entry.level)}>{String(entry.level || "INFO").toUpperCase()}</Badge>
                    </td>
                    <td className="px-2 py-3">
                      <Badge className={entry.type === "error" ? tone.red : tone.green}>{entry.type || "log"}</Badge>
                    </td>
                    <td className="px-2 py-3">
                      {entry.type === "http" ? (
                        <span className="font-bold">
                          {entry.method || "GET"} · {entry.statusCode ?? "—"}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">{entry.error_code || entry.error_name || "—"}</span>
                      )}
                    </td>
                    <td className="max-w-[280px] truncate px-2 py-3 font-extrabold" title={summary}>
                      {summary}
                    </td>
                    <td className="whitespace-nowrap px-2 py-3 text-muted-foreground">
                      {entry.duration_ms != null ? `${entry.duration_ms}ms` : "—"}
                    </td>
                    <td className="max-w-[140px] truncate px-2 py-3 font-mono text-xs text-muted-foreground" title={entry.requestId || ""}>
                      {entry.requestId || "—"}
                    </td>
                    <td className="px-2 py-3 text-right">
                      <Link to={`/admin/logs/${entry.id}`} data-testid={`admin-log-${entry.id}-link`}>
                        <Button variant="outline" className="rounded-lg">
                          View
                        </Button>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data && !data.items?.length ? (
            <EmptyState title="No application logs" body="HTTP and error events will appear here as traffic hits the API." testId="admin-logs-empty" />
          ) : null}
        </div>
        <Pagination
          page={data?.page || 1}
          totalPages={data?.total_pages || 1}
          total={data?.total || 0}
          limit={data?.limit || PAGE_SIZE}
          hasPrev={data?.has_prev}
          hasNext={data?.has_next}
          testId="admin-logs"
          onPageChange={(next) => {
            setPage(next);
            load({ nextPage: next });
          }}
        />
      </section>
    </section>
  );
}

function LogDetailPage({ logId }) {
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError("");
        const result = await apiGet(`/admin/logs/${logId}`);
        if (!cancelled) setEntry(result);
      } catch {
        if (!cancelled) {
          setEntry(null);
          setError("Could not load this log entry.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [logId]);

  if (!entry && !error) return <p className="text-sm text-muted-foreground">Loading log…</p>;

  if (error) {
    return (
      <section className="space-y-4" data-testid="admin-log-detail-error">
        <Link to="/admin/logs" className="inline-flex items-center gap-2 text-sm font-bold text-[#173B2F]" data-testid="admin-log-back-link">
          <ArrowLeft className="h-4 w-4" />
          Back to logs
        </Link>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>
      </section>
    );
  }

  const title =
    entry.type === "http"
      ? `${entry.method || "GET"} ${entry.endpoint || entry.path || "/"}`
      : entry.message || entry.error_name || "Error";

  return (
    <section className="space-y-5" data-testid="admin-log-detail-page">
      <Link to="/admin/logs" className="inline-flex items-center gap-2 text-sm font-bold text-[#173B2F]" data-testid="admin-log-back-link">
        <ArrowLeft className="h-4 w-4" />
        Back to logs
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-extrabold uppercase text-muted-foreground">{entry.event || entry.type || "log"}</p>
          <h2 className="break-all text-2xl font-extrabold">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{formatWhen(entry.created_at)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge className={logLevelTone(entry.level)}>{String(entry.level || "INFO").toUpperCase()}</Badge>
          <Badge className={entry.type === "error" ? tone.red : tone.green}>{entry.type || "log"}</Badge>
        </div>
      </div>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Status" value={entry.statusCode ?? "—"} testId="admin-log-detail-status" />
        <Metric label="Duration" value={entry.duration_ms != null ? `${entry.duration_ms}ms` : "—"} testId="admin-log-detail-duration" />
        <Metric label="Request ID" value={entry.requestId || "—"} testId="admin-log-detail-request" />
        <Metric label="User" value={entry.userId || "—"} testId="admin-log-detail-user" />
      </section>

      <section className="rounded-lg border bg-white p-4 shadow-sm">
        <h3 className="text-sm font-extrabold uppercase text-muted-foreground">Summary</h3>
        <dl className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-bold uppercase text-muted-foreground">Service</dt>
            <dd className="font-bold">{entry.service || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold uppercase text-muted-foreground">Environment</dt>
            <dd className="font-bold">{entry.environment || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold uppercase text-muted-foreground">Merchant</dt>
            <dd className="font-bold">{entry.merchantId || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold uppercase text-muted-foreground">Trace ID</dt>
            <dd className="break-all font-mono text-sm">{entry.traceId || "—"}</dd>
          </div>
          {entry.error_name ? (
            <div>
              <dt className="text-xs font-bold uppercase text-muted-foreground">Error</dt>
              <dd className="font-bold">
                {entry.error_name}
                {entry.error_code ? ` (${entry.error_code})` : ""}
              </dd>
            </div>
          ) : null}
          {entry.message ? (
            <div className="sm:col-span-2">
              <dt className="text-xs font-bold uppercase text-muted-foreground">Message</dt>
              <dd className="font-bold">{entry.message}</dd>
            </div>
          ) : null}
        </dl>
      </section>

      {entry.stack ? (
        <section className="rounded-lg border bg-white p-4 shadow-sm">
          <h3 className="text-sm font-extrabold uppercase text-muted-foreground">Stack</h3>
          <pre className="mt-3 max-h-72 overflow-auto rounded-md border bg-[#F8F5EE] p-3 text-xs leading-relaxed text-[#173B2F]">
            {entry.stack}
          </pre>
        </section>
      ) : null}

      <section className="rounded-lg border bg-white p-4 shadow-sm">
        <h3 className="text-sm font-extrabold uppercase text-muted-foreground">Raw log</h3>
        <pre className="mt-3 max-h-96 overflow-auto rounded-md border bg-[#F8F5EE] p-3 text-xs leading-relaxed text-[#173B2F]" data-testid="admin-log-detail-raw">
          {JSON.stringify(entry, null, 2)}
        </pre>
      </section>
    </section>
  );
}
